(function () {
  /* ── Helpers ── */
  /* Number.isFinite, because a money formatter must never print "CHF NaN".
     Anything undefined reaching it - a discount application without a
     total_allocated_amount, a partial cart from a failed request - used to
     render literally as NaN on the page. Falling back to zero is wrong by at
     most the amount we never had, and it is not gibberish. */
  const formatMoney = (cents) => {
    const n = Number(cents);
    return (
      "CHF " +
      (Number.isFinite(n) ? n / 100 : 0)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    );
  };

  const drawer = document.getElementById("cart-drawer");
  const overlay = document.getElementById("cart-drawer-overlay");
  const closeBtn = document.getElementById("cart-drawer-close");
  const itemsEl = document.getElementById("cart-drawer-items");
  const footerEl = document.getElementById("cart-drawer-footer");
  const totalEl = document.getElementById("cart-drawer-total");
  const subtotalRowEl = document.getElementById("cart-drawer-subtotal-row");
  const subtotalEl = document.getElementById("cart-drawer-subtotal");
  const discountsEl = document.getElementById("cart-drawer-discounts");
  const loading = document.getElementById("cart-drawer-loading");

  /* ── Free-shipping progress ──
     The threshold is carried on the banner rather than duplicated here, so the
     theme setting stays the only place it is written down. */
  const shipBanner = document.querySelector("[data-free-ship-min]");
  const shipMsgEl = shipBanner && shipBanner.querySelector("[data-free-ship-msg]");
  const freeShipMin = shipBanner
    ? parseInt(shipBanner.dataset.freeShipMin, 10) || 0
    : 0;

  /* isEmpty is passed rather than inferred from total === 0. An empty basket
     and a basket discounted to nothing are not the same sentence, and only the
     caller knows which one it is looking at.

     On an empty cart the shortfall wording is nonsense - "you still need CHF
     99" reads as a debt against a purchase that has not started. The offer is
     stated instead, which is the same fact framed as a reason to shop. */
  function paintFreeShipping(total, isEmpty) {
    if (!shipMsgEl || !freeShipMin) return;

    if (isEmpty) {
      shipMsgEl.textContent =
        "Kostenloser Versand ab " + formatMoney(freeShipMin) + ".";
      return;
    }

    const short = freeShipMin - total;
    shipMsgEl.textContent =
      short > 0
        ? "Noch " + formatMoney(short) + " bis zum kostenlosen Versand."
        : "Schnelle und kostenlose Lieferung";
  }

  /* ── Open / Close ── */
  function openDrawer() {
    drawer.classList.add("open");
    overlay.classList.add("active");
    document.body.style.overflow = "hidden";
    renderCart();
  }
  function closeDrawer() {
    drawer.classList.remove("open");
    overlay.classList.remove("active");
    document.body.style.overflow = "";
  }

  overlay.addEventListener("click", closeDrawer);
  closeBtn.addEventListener("click", closeDrawer);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeDrawer();
  });

  /* ── Trigger: click on .cart link ── */
  document.addEventListener("click", function (e) {
    const cartLink = e.target.closest(".cart a");
    if (cartLink) {
      e.preventDefault();
      openDrawer();
    }
  });

  /* ── Fetch & Render Cart ── */
  /* /cart.js carries no compare_at_price on its line items, so it is fetched
     separately from templates/cart.compare.liquid - one request, keyed by line
     item key. Failure is not fatal: the drawer just draws no struck-through
     prices, which is what it did before this existed. */
  async function fetchCompareMap() {
    try {
      const res = await fetch("/cart?view=compare", { credentials: "same-origin" });
      if (!res.ok) return {};
      const data = await res.json();
      return data.compare || {};
    } catch (_e) {
      return {};
    }
  }

  async function renderCart() {
    loading.classList.add("active");
    try {
      const [cart, compareMap] = await Promise.all([
        fetch("/cart.js").then(function (r) { return r.json(); }),
        fetchCompareMap(),
      ]);
      renderItems(cart, compareMap);
    } catch (err) {
      itemsEl.innerHTML =
        '<p class="cart-drawer__empty fs-base text-center text-gray">Warenkorb konnte nicht geladen werden.</p>';
    } finally {
      loading.classList.remove("active");
    }
  }

  /* Only shown once something has actually moved the total - an empty
     Zwischensumme row sitting above an identical Gesamt is just noise. */
  /* A discount row is only drawn when its amount is a real number. Anything
     unusable - a discount application that arrived without a
     total_allocated_amount, a partial cart - takes the row out rather than
     printing a figure nobody can trust. A missing line is quieter than a wrong
     one, and the Gesamt below is still correct either way.

     The Zwischensumme row follows the same fate: it exists to explain the gap
     between subtotal and total, so with nothing to explain it is noise. */
  function paintDrawerDiscounts(cart, subtotal, total) {
    const applications = cart.cart_level_discount_applications || [];
    const gap = Math.max(0, subtotal - total);

    const rows = applications
      .map(function (d) {
        const raw = Number(d.total_allocated_amount);

        // The one case where it can be reconstructed: a single application
        // accounts for the whole gap between the two totals.
        const amount =
          Number.isFinite(raw) && raw > 0
            ? raw
            : applications.length === 1 && gap > 0
              ? gap
              : null;

        if (amount === null || !d.title) return null;
        return { title: d.title, amount: amount };
      })
      .filter(Boolean);

    if (subtotalRowEl) subtotalRowEl.hidden = rows.length === 0;
    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);

    if (!discountsEl) return;

    discountsEl.innerHTML = rows
      .map(function (r) {
        return `<div class="cart-drawer__discount-row flex item-center justify-between">
                  <span class="fs-sm fw-regular">${r.title}</span>
                  <span class="fs-sm fw-semibold">− ${formatMoney(r.amount)}</span>
                </div>`;
      })
      .join("");
  }

  function renderItems(cart, compareMap) {
    compareMap = compareMap || {};
    /* Filter out upsell products */
    const items = cart.items.filter(
      (item) => (item.product_type || "").toLowerCase() !== "upsell",
    );

    if (!items.length) {
      itemsEl.innerHTML =
        '<p class="cart-drawer__empty fs-base text-center text-gray">Ihr Warenkorb ist leer.</p>';
      footerEl.style.display = "none";
      paintFreeShipping(0, true);
      return;
    }

    footerEl.style.display = "block";

    /* Straight from the cart rather than summed here: these are the figures
       checkout will charge, and they are the same two the cart page prints as
       Zwischensumme and Gesamtbetrag. */
    const subtotal = Number(cart.original_total_price || 0);
    const total = Number(cart.total_price || 0);

    totalEl.textContent = formatMoney(total);
    paintFreeShipping(total, false);
    paintDrawerDiscounts(cart, subtotal, total);

    /* Add-ons are drawn inside the parent they belong to, exactly as the cart
       page does. The link is the _addon_for_id property, with the older
       _addon_for title as a fallback for carts created before the id existed.

       An add-on whose parent is not in this cart is NOT dropped - it falls
       through and renders as an ordinary card. A line may move; it may never
       disappear off a page while still being charged. */
    /* _bundle alone does not mark an add-on - the parent line carries it too.
       Only _addon_for / _addon_for_id say "this line is an accessory". */
    function addonMarker(item) {
      const props = item.properties || {};
      return props._addon_for_id || props._addon_for || "";
    }

    function belongsTo(addon, parent) {
      const props = addon.properties || {};
      const parentProps = parent.properties || {};

      // Bundle first: it is the only thing that separates two lines of the same
      // product in different variants, where the product id matches both.
      if (props._bundle) {
        return String(props._bundle) === String(parentProps._bundle || "");
      }
      if (props._addon_for_id) {
        return String(props._addon_for_id) === String(parent.product_id || "");
      }
      return (
        String(props._addon_for || "").trim() ===
        String(parent.product_title || parent.title || "").trim()
      );
    }

    const parents = items.filter(function (i) { return !addonMarker(i); });
    const addons = items.filter(function (i) { return Boolean(addonMarker(i)); });

    const childrenOf = new Map();
    const claimed = new Set();

    parents.forEach(function (parent) {
      const mine = addons.filter(function (a) { return belongsTo(a, parent); });
      mine.forEach(function (a) { claimed.add(a.key); });
      childrenOf.set(parent.key, mine);
    });

    const orphans = addons.filter(function (a) { return !claimed.has(a.key); });

    itemsEl.innerHTML = parents
      .map(function (parent) {
        const kids = childrenOf.get(parent.key) || [];
        const nest = kids.length
          ? `<div class="cart-item__addons-wrapper">
               <p class="cart-item__addons-header">
                 Zubeh\u00f6r
                 <span class="cart-item__addons-count">${kids.length}</span>
               </p>
               <div class="cart-item__addons flex flex-col">
                 ${kids.map(function (k) { return renderAddonRow(k, compareMap); }).join("")}
               </div>
             </div>`
          : "";

        return `<div class="cart-item cart-drawer__item" data-key="${parent.key}">
                  ${renderCardRow(parent, compareMap)}
                  ${nest}
                </div>`;
      })
      .concat(
        orphans.map(function (o) {
          return `<div class="cart-item cart-drawer__item" data-key="${o.key}">
                    ${renderCardRow(o, compareMap)}
                  </div>`;
        })
      )
      .join("");
  }

  /* One place that knows what a line looks like; the parent card and the
     add-on row differ only by the --addon modifiers. */
  function priceParts(item, compareMap) {
    const qty = Math.max(1, Number(item.quantity || 1));
    const currentLine = Number(item.line_price || 0);
    const totalDiscount = Number(item.total_discount || 0);

    /* What the line is worth before any cart-level code. The product page and
       the cart page both show this; a code applied to the whole order is broken
       out once in the footer rather than folded into every line. */
    const lineNow = Number(item.original_line_price || item.line_price || 0);

    const compareUnit = Number(compareMap[item.key] || 0);
    const compareTotal = Math.max(
      compareUnit * qty,
      Number(item.original_line_price || 0),
      totalDiscount > 0 ? currentLine + totalDiscount : 0
    );

    /* The badge is the PRODUCT's discount - compare-at against its own price -
       and nothing else, so a promo code cannot change it. Math.floor matches
       the cart page's Liquid, where `divided_by` on two integers truncates. */
    const badgeNow = Number(item.original_price || item.price || 0);
    const percentOff =
      compareUnit > badgeNow && compareUnit > 0
        ? Math.floor(((compareUnit - badgeNow) / compareUnit) * 100)
        : 0;

    return {
      lineNow: lineNow,
      compare:
        compareTotal > lineNow && compareTotal > 0
          ? `<span class="cart-item__price-original">${formatMoney(compareTotal)}</span>`
          : "",
      badge:
        percentOff > 0
          ? `<span class="cart-item__badge fw-bold text-white">-${percentOff}%</span>`
          : "",
    };
  }

  function imageFor(item) {
    return item.image
      ? item.image.replace(/(\.[^.]+)$/, "_160x160$1")
      : 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"/>';
  }

  /* The drawer's own handlers select on cart-drawer__qty-btn, -qty-num and
     -item-remove, so those stay alongside the visual classes. cart-sb.js
     deliberately ignores every click inside #cart-drawer. */
  function controls(item, extraClass, trailing) {
    return `<div class="cart-item__controls ${extraClass}">
              <div class="qty-wrapper flex item-center">
                <button class="qty-btn cart-drawer__qty-btn flex item-center justify-center" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">\u2212</button>
                <span class="qty-input cart-drawer__qty-num text-center fw-bold">${item.quantity}</span>
                <button class="qty-btn cart-drawer__qty-btn flex item-center justify-center" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
              </div>
              <button class="qty-btn cart-item__remove cart-drawer__item-remove flex item-center justify-center" data-key="${item.key}" aria-label="Remove item">
                <svg width="18" height="18" id="bin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                </svg>
              </button>
              ${trailing || ""}
            </div>`;
  }

  function renderCardRow(item, compareMap) {
    const p = priceParts(item, compareMap);

    /* Price under the meta, which is the cart page's own layout. It was tried
       on the controls row instead, to fill the space a ~380px drawer leaves
       empty beside a short price - but the drawer is only that wide on a
       desktop. On a phone it is nearer 340px, and the quantity pill, the bin
       and the price stopped fitting on one line, so the price was clipped at
       the edge. One layout that always fits beats two that mostly do. */
    return `<div class="cart-item__row">
              <div class="cart-item__media">
                ${p.badge}
                <img class="cart-item__img" src="${imageFor(item)}" alt="${item.product_title}" loading="lazy">
              </div>
              <div class="cart-item__details">
                <div class="cart-item__head">
                  <div class="cart-item__headline">
                    <h3 class="cart-item__name"><a href="${item.url}">${item.product_title}</a></h3>
                    ${item.variant_title ? `<p class="cart-item__meta">${item.variant_title}</p>` : ""}
                  </div>
                  <div class="cart-price-box">
                    <span class="cart-item__price">${formatMoney(p.lineNow)}</span>
                    ${p.compare}
                  </div>
                </div>
                ${controls(item, "")}
              </div>
            </div>`;
  }

  function renderAddonRow(item, compareMap) {
    const p = priceParts(item, compareMap);
    return `<div class="cart-item__addon-row" data-key="${item.key}">
              <div class="cart-item__media cart-item__media--addon">
                <img class="cart-item__img" src="${imageFor(item)}" alt="${item.product_title}" loading="lazy">
              </div>
              <div class="cart-item__details">
                <div class="cart-item__head">
                  <div class="cart-item__headline">
                    <h4 class="cart-item__name cart-item__name--addon"><a href="${item.url}">${item.product_title}</a></h4>
                    ${item.variant_title ? `<p class="cart-item__meta">${item.variant_title}</p>` : ""}
                  </div>
                  <div class="cart-price-box">
                    <span class="cart-item__price cart-item__price--addon">${formatMoney(p.lineNow)}</span>
                    ${p.compare}
                  </div>
                </div>
                ${controls(item, "cart-item__controls--addon")}
              </div>
            </div>`;
  }

  /* ── Qty & Remove events ── */
  itemsEl.addEventListener("click", async function (e) {
    const qtyBtn = e.target.closest(".cart-drawer__qty-btn");
    const removeBtn = e.target.closest(".cart-drawer__item-remove");

    if (qtyBtn) {
      const key = qtyBtn.dataset.key;
      const action = qtyBtn.dataset.action;
      const itemEl = qtyBtn.closest(".cart-drawer__item");
      const qtyEl = itemEl.querySelector(".cart-drawer__qty-num");
      const current = parseInt(qtyEl.textContent, 10);
      const newQty =
        action === "increase" ? current + 1 : Math.max(0, current - 1);
      await updateItem(key, newQty);
    }

    if (removeBtn) {
      await updateItem(removeBtn.dataset.key, 0);
    }
  });

  async function removeLinkedAddonItems(key) {
    try {
      const cartRes = await fetch("/cart.js");
      if (!cartRes.ok) return;
      const cart = await cartRes.json();
      const parent = cart.items.find(function (item) {
        return item.key === key;
      });
      if (!parent) return;

      const parentTitle = (parent.product_title || parent.title || "").trim();
      const parentProductId = String(parent.product_id || "");
      const parentBundle = String((parent.properties || {})._bundle || "");
      if (!parentTitle) return;

      const addonKeys = cart.items
        .filter(function (item) {
          if (item.key === key) return false;
          const props = item.properties || {};
          // Bundle first: it is the only thing that separates two lines of the same
          // product in different variants. The id and title are for carts that
          // predate it, and are never consulted for an add-on that has a bundle.
          if (props._bundle) return String(props._bundle) === parentBundle;
          if (props._addon_for_id) return String(props._addon_for_id) === parentProductId;
          return Boolean(props._addon_for) && String(props._addon_for).trim() === parentTitle;
        })
        .map(function (item) {
          return item.key;
        });

      for (const addonKey of addonKeys) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: addonKey, quantity: 0 }),
        });
      }
    } catch (err) {
      // ignore background addon removal failures
    }
  }

  async function updateItem(key, qty) {
    loading.classList.add("active");
    try {
      if (qty === 0) {
        await removeLinkedAddonItems(key);
      }

      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      const cart = await res.json();
      renderItems(cart);
      updateHeaderCount(cart);
    } catch (err) {
      console.error("Cart update failed", err);
    } finally {
      loading.classList.remove("active");
    }
  }

  /* ── Update header cart count badge ── */
  function updateHeaderCount(cart) {
    const badges = document.querySelectorAll("[data-sb-cart-count]");
    const items = cart.items.filter(
      (i) => (i.product_type || "").toLowerCase() !== "upsell",
    );
    const count = items.reduce((s, i) => s + i.quantity, 0);
    badges.forEach((b) => {
      b.textContent = count;
    });
  }

  /* ── Auto-open after Add To Cart ── */
  document.addEventListener("click", async function (e) {
    const atcBtn = e.target.closest(
      '[data-add-to-cart], .product-form__submit, [name="add"]',
    );
    if (!atcBtn) return;

    const form =
      atcBtn.closest('form[action*="/cart/add"]') || atcBtn.closest("form");
    if (!form) return;

    e.preventDefault();
    loading.classList.add("active");

    try {
      const formData = new FormData(form);
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });
      if (res.ok) {
        openDrawer();
      } else {
        form.submit();
      }
    } catch {
      form.submit();
    } finally {
      loading.classList.remove("active");
    }
  });

  /* ── Expose globally ── */
  window.SBCartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    refresh: renderCart,
  };
})();
